// Area supplements (MCO, etc.) when Add Plan step 1 has no base crop offers.
// Availability = InsuranceOffer (AreaRate often empty before underlying SCD).

import {
  INSURANCE_PLAN_NAME_RP,
  INSURANCE_PLAN_NAME_RP_HPE,
  INSURANCE_PLAN_NAME_YP,
} from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";

import { MCO_PLAN_NAMES, mcoPlanCodeFromOfferName, probeMcoInsuranceOffers } from "./mcoAreaInputs.js";
import {
  defaultMcoBandsForYear,
  num,
  pick,
  resolveMcoPracticeName,
  UNDERLYING_REQUIREMENT_DISCLAIMER,
} from "./mcoShared.js";

export { UNDERLYING_REQUIREMENT_DISCLAIMER };

const MCO_NAME_SET = new Set(Object.values(MCO_PLAN_NAMES).flat());

const MCO_VARIANTS = {
  68: { basePlanName: INSURANCE_PLAN_NAME_RP, label: "MCO", subLabel: "Rev Prot underlying" },
  67: { basePlanName: INSURANCE_PLAN_NAME_YP, label: "MCO", subLabel: "Yield Prot underlying" },
  69: { basePlanName: INSURANCE_PLAN_NAME_RP_HPE, label: "MCO", subLabel: "Rev Prot w HPE underlying" },
};

function cropFromParams(farmParameters, countyParameters) {
  const reinsuranceYear = String(
    countyParameters?.reinsurance_year || farmParameters?.reinsurance_year || farmParameters?.year || ""
  );
  return {
    crop: {
      stateName: farmParameters?.state_name || farmParameters?.location_state_name,
      countyName: farmParameters?.county_name || farmParameters?.location_county_name,
      commodityName: farmParameters?.commodity_name,
      type_name: farmParameters?.type_name,
      practice_name: farmParameters?.practice_name,
      acres: num(farmParameters?.reported_acreage),
      yieldPerAcre: num(farmParameters?.approved_yield),
      sharePct: farmParameters?.share_percent,
      price: countyParameters?.projected_price,
    },
    reinsuranceYear,
    geoReady: Boolean(
      reinsuranceYear &&
      (farmParameters?.state_name || farmParameters?.location_state_name) &&
      (farmParameters?.county_name || farmParameters?.location_county_name) &&
      farmParameters?.commodity_name
    ),
  };
}

/** MCO rows from an InsuranceOffer list (selectInsuranceOffer or wide ADM query). */
export function mcoOfferRowsFromList(rows) {
  return (rows || []).filter((row) =>
    MCO_NAME_SET.has(String(pick(row, "insurance_plan_name", "Insurance_plan_name") || "").trim())
  );
}

/**
 * Turn raw InsuranceOffer rows into Add Plan supplement buttons.
 * @returns {{ kind: 'mco', planCode: string, basePlanName: string, label: string, subLabel: string, insurance_plan_name: string, practice_name: string, bands: object[] }[]}
 */
export function collectMcoSupplementOffers(offerRows, crop, reinsuranceYear) {
  const farmPractice = resolveMcoPracticeName(crop.practice_name);
  const found = [];
  const seen = new Set();

  for (const row of offerRows || []) {
    const insurance_plan_name = pick(row, "insurance_plan_name", "Insurance_plan_name");
    const planCode = mcoPlanCodeFromOfferName(insurance_plan_name);
    if (!planCode) continue;

    const practice_name = resolveMcoPracticeName(pick(row, "practice_name", "Practice_name"));
    if (!practice_name) continue;
    if (farmPractice && farmPractice !== practice_name) continue;

    const key = `${planCode}|${practice_name}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const variant = MCO_VARIANTS[planCode] || MCO_VARIANTS[68];
    const bands = defaultMcoBandsForYear(reinsuranceYear);

    found.push({
      kind: "mco",
      planCode,
      basePlanName: variant.basePlanName,
      label: variant.label,
      subLabel: `${variant.subLabel} · ${practice_name}`,
      practice_name,
      insurance_plan_name,
      bands,
    });
  }

  return found;
}

export async function loadAreaSupplementOffers({ farmParameters, countyParameters, insuranceOffers }) {
  const { crop, reinsuranceYear, geoReady } = cropFromParams(farmParameters, countyParameters);
  if (!geoReady) {
    console.warn("loadAreaSupplementOffers: geo not ready", crop);
    return [];
  }

  let offerRows = mcoOfferRowsFromList(insuranceOffers);
  if (!offerRows.length) {
    offerRows = await probeMcoInsuranceOffers(crop, reinsuranceYear);
  }

  const found = collectMcoSupplementOffers(offerRows, crop, reinsuranceYear);

  if (!found.length) {
    console.warn("loadAreaSupplementOffers: no MCO InsuranceOffer rows", crop, reinsuranceYear);
  }

  return found;
}
